import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../lib/utils';

interface RichContentViewerProps {
  content: string;
  className?: string;
}

export const RichContentViewer: React.FC<RichContentViewerProps> = ({ content, className }) => {
  return (
    <div className={cn("rich-content-viewer prose prose-sm max-w-none dark:prose-invert text-black dark:text-white", className)}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          img: ({ node, ...props }) => (
            <img 
              {...props} 
              alt={props.alt || "Content image"}
              className="rounded-custom shadow-sm max-h-[400px] object-contain my-4" 
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ),
          a: ({ node, ...props }) => (
            <a 
              {...props} 
              className="text-brand-primary hover:underline font-bold" 
              target="_blank" 
              rel="noopener noreferrer" 
            />
          ),
          // Simple video embedding if the link looks like a video
          p: ({ node, children, ...props }) => {
            // Safer text extraction from React children
            const getStaticText = (childList: any, depth = 0): string => {
              if (depth > 10) return ''; // Prevent infinite recursion
              
              const childrenArray = React.Children.toArray(childList) as any[];
              return childrenArray.reduce((acc: string, child: any) => {
                if (typeof child === 'string' || typeof child === 'number') return acc + String(child);
                if (React.isValidElement(child) && (child.props as any).children) {
                  return acc + getStaticText((child.props as any).children, depth + 1);
                }
                return acc;
              }, '');
            };

            const contentString = getStaticText(children);
            
            // Check for YouTube links - more specific regex to avoid capturing the whole paragraph
            const ytRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=)?([a-zA-Z0-9_-]{11})/;
            const ytMatch = contentString.match(ytRegex);
            
            // Only embed if the link is the ONLY thing in the paragraph, or at least dominant
            if (ytMatch && ytMatch[1] && contentString.trim() === ytMatch[0].trim()) {
              return (
                <div className="aspect-video w-full my-4 rounded-custom overflow-hidden shadow-lg border border-gray-100">
                  <iframe
                    src={`https://www.youtube.com/embed/${ytMatch[1]}`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="YouTube video player"
                  />
                </div>
              );
            }
            
            return <p {...props} className="mb-4 last:mb-0">{children}</p>;
          }
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
